import React, { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { collection, query, where, getDocs, documentId } from 'firebase/firestore';
import { db } from "../../firebaseConfig";
import { useAuth } from '../../AuthContext';
import { useSubAgentAuth } from '../../pages/Clients/SubAgent/SubAgentAuthContext';
import { useAppSelector } from '../../redux/store';
import { fetchLeadStageTimelineAnalytics, LeadData } from './Function';
import getCurrentScreen from '../../Helper/GetScreen';

// Format large numbers with K/M suffix
const formatYAxisTick = (value: number): string => {
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
  return value.toString();
};

// Format tooltip values with commas
const formatTooltipValue = (value: number) => {
  return value.toLocaleString();
};

interface UserOption {
  id: string;
  name: string;
  department?: string;
  departmentId?: string;
}

interface DepartmentOption {
  id: string;
  name: string;
}

interface CallLeadAnalyticsProps {
  settings: any;
}

const StageTimelineAnalytics: React.FC<CallLeadAnalyticsProps> = ({ settings }) => {
  const [isParameterOpen, setIsParameterOpen] = useState(false);
  const [isStaffOpen, setIsStaffOpen] = useState(false);
  const [isDepartmentOpen, setIsDepartmentOpen] = useState(false);

  const { userData, isAdmin, isManager, isStaff, currentUser } = useAuth();
  const { currentSubAgent, subAgentData } = useSubAgentAuth();
  const currentScreen = getCurrentScreen(useAppSelector(state => state.screen.currentScreen), isAdmin, isManager, isStaff);

  const [timePeriod, setTimePeriod] = useState<string>('full');
  const [startTime, setStartTime] = useState<string>();
  const [endTime, setEndTime] = useState<string>();

  // Department state
  const [departments, setDepartments] = useState<DepartmentOption[]>([]);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<string>('all');
  const [selectedUser, setSelectedUser] = useState<UserOption[]>([]);
  const [stageParameter, setStageParameter] = useState<string>();
  const [leadData, setLeadData] = useState<LeadData>();

  // Additional state for user management
  const [allUsers, setUsers] = useState<UserOption[]>([]);
  const [nodata, setNodata] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  // Fetch departments
  useEffect(() => {
    const fetchDepartments = async () => {
      if (!userData?.companyId) return;

      try {
        // First fetch all users to get their department IDs
        let usersQuery = query(
          collection(db, "users"),
          where("companyId", "==", userData?.companyId)
        );

        // Apply role-based filtering to users first
        if (isManager && currentScreen === 'Manager') {
          usersQuery = query(
            collection(db, "users"),
            where("companyId", "==", userData?.companyId),
            where("managerIds", "array-contains", currentUser?.uid)
          );
        } else if (isStaff && currentScreen === 'Staff') {
          usersQuery = query(
            collection(db, "users"),
            where("companyId", "==", userData?.companyId),
            where(documentId(), "==", currentUser?.uid)
          );
        }

        const usersSnapshot = await getDocs(usersQuery);
        const userDepartmentIds = new Set(
          usersSnapshot.docs
            .map(doc => doc.data().departmentId)
            .filter(deptId => deptId) // Remove null/undefined values
        );

        // Now fetch departments, but only include those that have staff
        const companyDepartmentsSnapshot = await getDocs(
          collection(db, `companies/${userData.companyId}/departments`)
        );
        
        const departmentsWithStaff = companyDepartmentsSnapshot.docs
          .filter(doc => userDepartmentIds.has(doc.id))
          .map(doc => ({
            id: doc.id,
            name: doc.data().department
          }))
          .sort((a, b) => a.name.localeCompare(b.name));

        setDepartments(departmentsWithStaff);
      } catch (error) {
        console.error("Error fetching departments:", error);
      }
    };

    fetchDepartments();
  }, [userData?.companyId, isManager, isStaff, currentScreen, currentUser?.uid]);

  // Fetch assigned to options
  const fetchAssignedToOptions = React.useCallback(async () => {
    if (!userData?.companyId) return;

    try {
      // Remove department filter from the query - fetch all users first
      let usersQuery = query(
        collection(db, "users"),
        where("companyId", "==", userData?.companyId)
      );

      // Rest of role-based filtering...
      if (isManager && currentScreen === 'Manager') {
        usersQuery = query(
          collection(db, "users"),
          where("companyId", "==", userData?.companyId),
          where("managerIds", "array-contains", currentUser?.uid)
        );
      } else if (isStaff && currentScreen === 'Staff') {
        usersQuery = query(
          collection(db, "users"),
          where("companyId", "==", userData?.companyId),
          where(documentId(), "==", currentUser?.uid)
        );
      }

      const usersSnapshot = await getDocs(usersQuery);
      const fetchedUsers = usersSnapshot.docs.map((doc) => ({
        id: doc.id,
        name: doc.data().name,
        department: doc.data().department, // Include department name for display
        departmentId: doc.data().departmentId // Include departmentId for filtering
      }));

      if (fetchedUsers.length === 0) {
        setToastMessage("No users found in your team");
        setShowToast(true);
        setNodata(true);
        setUsers([]);
        setSelectedUser([]);
      } else {
        setUsers(fetchedUsers);
        setNodata(false);
        // Update selected users based on department filter
        if (selectedDepartmentId !== 'all') {
          const departmentUsers = fetchedUsers.filter(user => user.departmentId === selectedDepartmentId);
          setSelectedUser(departmentUsers);
        } else {
          setSelectedUser([]);
        }
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  }, [userData?.companyId, isManager, isStaff, currentScreen, currentUser?.uid, selectedDepartmentId]);

  React.useEffect(() => {
    fetchAssignedToOptions();
  }, [fetchAssignedToOptions]);

  // Auto-hide toast after 3 seconds
  useEffect(() => {
    if (showToast) {
      const timer = setTimeout(() => {
        setShowToast(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showToast]);

  // Initialize dates
  useEffect(() => {
    const tempStartTime = new Date();
    tempStartTime.setMonth(0); // January
    tempStartTime.setDate(1);
    tempStartTime.setHours(5, 30, 0, 0);
    const tempStartTimeString = tempStartTime.toISOString();

    const tempEndTime = new Date();
    tempEndTime.setDate(tempEndTime.getDate() + 1);
    tempEndTime.setHours(5, 29, 59, 0);
    const tempEndTimeString = tempEndTime.toISOString();

    setStartTime(tempStartTimeString);
    setEndTime(tempEndTimeString);
  }, []);

  // Handle department change - automatically select all staff in that department
  const handleDepartmentChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const deptId = e.target.value;
    setSelectedDepartmentId(deptId);

    if (deptId === 'all') {
      setSelectedUser([]);
    } else {
      // Filter users for selected department and set them as selected
      const deptUsers = allUsers.filter(user => user.departmentId === deptId);
      setSelectedUser(deptUsers);
    }
  };

  // Fetch data whenever filters change
  useEffect(() => {
    const fetchData = async () => {
      if (allUsers.length > 0) {
        try {
          const data = await fetchLeadStageTimelineAnalytics({
            db,
            userData: userData || subAgentData,
            startTime: startTime || '',
            endTime: endTime || '',
            users: allUsers,
            currentScreen,
            selectedUser,
            stageParameter
          });
          setLeadData(data);
        } catch (error) {
          console.error("Error fetching call logs:", error);
        }
      }
    };

    fetchData();
  }, [userData?.companyId, startTime, endTime, allUsers, currentScreen, selectedUser, stageParameter]);

  const handleTimeline = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const tempStartTime = new Date();
    const tempEndTime = new Date();
    tempEndTime.setDate(tempEndTime.getDate() + 1);
    tempEndTime.setHours(5, 29, 59, 0);

    if (value === 'weeks') {
      tempStartTime.setDate(1);
      tempStartTime.setHours(5, 30, 0, 0);
      setTimePeriod('weeks');
    } else if (value === 'full') {
      tempStartTime.setMonth(0);
      tempStartTime.setDate(1);
      tempStartTime.setHours(5, 30, 0, 0);
      setTimePeriod('full');
    }

    setStartTime(tempStartTime.toISOString());
    setEndTime(tempEndTime.toISOString());
  }

  const handleStageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value === 'Clear') {
      setStageParameter('');
      setIsParameterOpen(false);
      return;
    }
    setStageParameter(e.target.value);
    setIsParameterOpen(false);
  }

  const handleSelectedUser = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (e.target.value === 'All Staff') {
      // If department is selected, include all users from that department
      if (selectedDepartmentId !== 'all') {
        const deptUsers = allUsers.filter(user => user.departmentId === selectedDepartmentId);
        setSelectedUser(deptUsers);
      } else {
        setSelectedUser([]);
      }
      return;
    }
    // Single user selection
    setSelectedUser([{ id: e.target.value, name: '' }]);
  };

  // Filter users based on selected department
  const filteredUsers = selectedDepartmentId === 'all'
    ? allUsers
    : allUsers.filter(user => user.departmentId === selectedDepartmentId);

  // Get the display value for the staff dropdown
  const getStaffDropdownValue = () => {
    if (selectedUser.length === 0) return 'All Staff';
    if (selectedUser.length === filteredUsers.length && selectedDepartmentId !== 'all') {
      return `All in ${departments.find(d => d.id === selectedDepartmentId)?.name || 'Department'}`;
    }
    if (selectedUser.length === 1) return selectedUser[0].id;
    return `${selectedUser.length} selected`;
  };

  // handle click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (isParameterOpen && !(e.target as HTMLElement).closest('.parameter-select')) {
        setIsParameterOpen(false);
      }
      if (isStaffOpen && !(e.target as HTMLElement).closest('.staff-select')) {
        setIsStaffOpen(false);
      }
      if (isDepartmentOpen && !(e.target as HTMLElement).closest('.department-select')) {
        setIsDepartmentOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isParameterOpen, isStaffOpen, isDepartmentOpen]);

  return (
    <div className="bg-white rounded-lg p-2 sm:p-3 md:p-4 shadow-sm h-full flex flex-col relative overflow-hidden">
      {/* Toast message */}
      {showToast && (
        <div className="absolute top-2 left-2 right-2 bg-red-500 text-white px-3 py-2 rounded-md shadow-lg z-50 text-xs sm:text-sm">
          {toastMessage}
        </div>
      )}
      
      {/* Header - Compact */}
      <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
        <div className="flex items-center justify-center w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 rounded-full flex-shrink-0" style={{
          background: "linear-gradient(180deg, #FFA782 0%, #FC583D 100%)",
        }}>
          <img src={`${process.env.PUBLIC_URL}/images/desktop/step.svg`} alt="Icon" className="w-3 h-3 sm:w-4 sm:h-4 md:w-6 md:h-6" />
        </div>
        <h2 className="text-sm sm:text-lg md:text-xl text-black font-medium truncate" style={{ fontFamily: "Montserrat", lineHeight: "1.2" }}>
          Stage Analytics
        </h2>
      </div>

      {/* Controls Section - Compact Grid Layout */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-2 mb-3">
        {/* Parameter Select */}
        <div className="relative parameter-select sm:col-span-1">
          <button
            onClick={() => setIsParameterOpen(!isParameterOpen)}
            className="w-full px-2 py-1.5 text-xs border border-cyan-500 rounded-md flex items-center justify-between bg-white"
          >
            <span className="truncate text-left">
              {stageParameter
                ? settings?.stages?.find((stage: { id: string; }) => stage.id === stageParameter)?.stage ?? 'No Stage'
                : 'Select Stage'
              }
            </span>
            <svg className="w-3 h-3 ml-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {isParameterOpen && (
            <div className="absolute left-0 top-full mt-1 p-2 bg-white border border-cyan-500 rounded-md shadow-lg z-30 w-full max-h-32 overflow-y-auto">
              <div className="flex items-center gap-2 text-xs mb-1">
                <input
                  type="radio"
                  id="clear"
                  name="stage"
                  value="Clear"
                  onChange={handleStageSelect}
                />
                <label htmlFor='clear' className="text-xs">Clear</label>
              </div>
              {settings?.stages?.length > 0 && settings.stages.map((stage: { id: string; stage: string; }) => (
                <div key={stage.id} className="flex items-center gap-2 text-xs mb-1">
                  <input
                    type="radio"
                    id={stage.id}
                    name="stage"
                    value={stage.id}
                    checked={stageParameter === stage.id}
                    onChange={handleStageSelect}
                  />
                  <label htmlFor={stage.id} className="truncate text-xs">{stage.stage}</label>
                </div>
              ))}
            </div>
          )}
        </div>

        {currentScreen !== 'Staff' && (
          <>
            {/* Department Select */}
            <div className="sm:col-span-1">
              <select
                className="w-full p-1.5 border border-cyan-600 text-xs rounded-md bg-white"
                value={selectedDepartmentId}
                onChange={handleDepartmentChange}
              >
                <option value="all">All Departments</option>
                {departments.map(dept => (
                  <option key={dept.id} value={dept.id}>{dept.name}</option>
                ))}
              </select>
            </div>

            {/* Staff Select */}
            <div className="sm:col-span-1">
              <select
                className="w-full p-1.5 border border-cyan-600 text-xs rounded-md bg-white"
                value={getStaffDropdownValue()}
                onChange={handleSelectedUser}
              >
                <option value="All Staff">
                  {selectedDepartmentId !== 'all'
                    ? `All in ${departments.find(d => d.id === selectedDepartmentId)?.name || 'Dept'}`
                    : 'All Staff'
                  }
                </option>
                {filteredUsers.map(user => (
                  <option key={user.id} value={user.id}>{user.name}</option>
                ))}
              </select>
            </div>
          </>
        )}

        {/* Time Period Selection */}
        <div className="flex items-center justify-center sm:justify-start lg:justify-end lg:col-span-1 xl:col-span-2">
          <div className="flex bg-gray-100 rounded-md p-0.5">
            <button
              onClick={() => handleTimeline({ target: { value: 'weeks' } } as any)}
              className={`px-2 py-1 text-xs rounded-md transition-all duration-200 ${timePeriod === 'weeks'
                ? 'bg-white text-cyan-600 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              MTD
            </button>
            <button
              onClick={() => handleTimeline({ target: { value: 'full' } } as any)}
              className={`px-2 py-1 text-xs rounded-md transition-all duration-200 ${timePeriod === 'full'
                ? 'bg-white text-cyan-600 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              YTD
            </button>
          </div>
        </div>
      </div>

      {/* Total Leads - Compact */}
      <div className="text-center sm:text-right mb-2">
        <span className="font-medium text-xs sm:text-sm">Total: {leadData?.totalLeads}</span>
      </div>

      {/* Chart Container - Takes remaining space */}
      <div className="flex-1 min-h-0">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={leadData?.timelineMapArray}
            barSize={12}
            barGap={2}
            margin={{ 
              top: 8, 
              right: 5, 
              left: 5, 
              bottom: 20
            }}
          >
            <CartesianGrid strokeDasharray="2 2" vertical={false} stroke="#f0f0f0" />
            <XAxis
              dataKey="timeline"
              scale="point"
              padding={{ left: 10, right: 10 }}
              tick={{ fill: '#666', fontSize: 10 }}
              axisLine={{ stroke: '#E5E7EB' }}
              tickFormatter={(value) => {
                // Truncate long labels for better fit
                const label = value.includes('|') ? value.split('|')[0] : value;
                return label.length > 6 ? label.substring(0, 6) + '...' : label;
              }}
              interval={0}
            />
            <YAxis
              tickFormatter={formatYAxisTick}
              tick={{ fill: '#666', fontSize: 10 }}
              axisLine={{ stroke: '#E5E7EB' }}
              tickLine={false}
              width={25}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#fff',
                border: '1px solid #E5E7EB',
                borderRadius: '4px',
                padding: '6px 8px',
                fontSize: '11px',
                maxWidth: '200px'
              }}
              formatter={(value: number) => formatTooltipValue(value)}
              labelFormatter={(label: string) => {
                if (label.includes('|')) {
                  const [weekLabel, dateRange] = label.split('|');
                  return `${weekLabel} (${dateRange})`;
                }
                return label;
              }}
              cursor={{ fill: 'rgba(0, 0, 0, 0.04)' }}
            />
            <Legend 
              wrapperStyle={{ 
                paddingTop: '8px',
                fontSize: '10px'
              }}
              iconSize={10}
            />
            {stageParameter && (
              <Bar
                dataKey="stageCount"
                fill="#00A6D9"
                radius={[2, 2, 0, 0]}
                name={settings?.stages?.find((stage: { id: string; }) => stage.id === stageParameter)?.stage ?? 'Selected Stage'}
                isAnimationActive={true}
                animationBegin={0}
                animationDuration={600}
                animationEasing="ease-out"
              />
            )}
            <Bar
              dataKey="totalCount"
              fill="#FFA782"
              radius={[2, 2, 0, 0]}
              name="Total Leads"
              isAnimationActive={true}
              animationBegin={0}
              animationDuration={600}
              animationEasing="ease-out"
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
};

export default StageTimelineAnalytics;